<template>
  <component
    :is="hasLink ? 'NuxtLink' : 'span'"
    data-component-name="AppTag"
    v-bind="hasLink ? { to: href, target } : null"
  >
    <span :class="variant">
      <slot />
    </span>
  </component>
</template>

<script setup lang="ts">
// components
import { NuxtLink } from '#components';

// types
import type { NuxtLinkProps } from '#app/components/nuxt-link';

// utils
import { isString } from '~/utils';

defineOptions({
  components: {
    NuxtLink,
  },
});

export interface AppTagProps {
  href?: string;
  target?: NuxtLinkProps['target'];
  variant?:
    | 'primary'
    | 'secondary'
    | 'tertiary'
    | 'image'
    | 'orange'
    | 'quinary';
}

const props = withDefaults(defineProps<AppTagProps>(), {
  variant: 'primary',
  target: '_blank',
});

defineSlots<{ default(): any }>();

const hasLink = computed(() => isString(props.href));
</script>

<style scoped lang="scss">
@import '$/functions/token';
@import '$/mixins/typography';
@import '$/mixins/flex';

[data-component-name='AppTag'] {
  display: inline-block;
  text-transform: capitalize;

  span {
    padding: 0.25rem 0.5rem;
    @include flex-center-center;
    gap: 0.25rem;

    transition: all 0.3s ease;
  }

  .primary {
    @include body-3;
    color: token('chip-primary-text');
    background-color: token('chip-primary-surf-cont');

    &:hover {
      color: token('chip-primary-text');
      background-color: color-mix(
        in srgb,
        token('neutral-hover') 16%,
        token('chip-primary-surf-cont')
      );
    }

    &:active {
      color: token('chip-primary-text');
      background-color: color-mix(
        in srgb,
        token('neutral-pressed') 32%,
        token('chip-primary-surf-cont')
      );
    }
  }

  .secondary {
    @include body-3;
    color: token('chip-secondary-text');
    background-color: token('chip-secondary-surf-cont');

    &:hover {
      color: token('chip-secondary-text');
      background-color: color-mix(
        in srgb,
        token('neutral-hover') 16%,
        token('chip-secondary-surf-cont')
      );
    }

    &:active {
      color: token('chip-secondary-text');
      background-color: color-mix(
        in srgb,
        token('neutral-pressed') 32%,
        token('chip-secondary-surf-cont')
      );
    }
  }

  .tertiary {
    @include body-3;
    color: token('chip-secondary-text');
    background-color: token('chip-tertiary-surf-cont');

    &:hover {
      background-color: color-mix(
        in srgb,
        token('neutral-hover') 16%,
        token('chip-tertiary-surf-cont')
      );
    }

    &:active {
      background-color: color-mix(
        in srgb,
        token('neutral-pressed') 32%,
        token('chip-tertiary-surf-cont')
      );
    }
  }

  .image {
    @include subtitle-3;
    color: token('text-reversed');
    background-color: token('surf-cont-secon-action');
    border: 2px solid token('outline-reversed');

    &:hover {
      background-color: color-mix(
        in srgb,
        token('accent-hover') 16%,
        token('surf-cont-secon-action')
      );
    }

    &:active {
      background-color: color-mix(
        in srgb,
        token('accent-pressed') 32%,
        token('surf-cont-secon-action')
      );
    }
  }

  .orange {
    @include subtitle-3;
    color: token('text-reversed');
    background-color: token('surf-cont-prim-action');

    &:hover {
      background-color: color-mix(
        in srgb,
        token('neutral-hover') 16%,
        token('surf-cont-prim-action')
      );
    }

    &:active {
      background-color: color-mix(
        in srgb,
        token('neutral-pressed') 32%,
        token('surf-cont-prim-action')
      );
    }
  }

  .quinary {
    @include body-3;
    color: token('text-primary');
    background-color: token('surf-cont-tertiary');

    &:hover {
      background-color: color-mix(
        in srgb,
        token('neutral-hover') 16%,
        token('surf-cont-tertiary')
      );
    }

    &:active {
      background-color: color-mix(
        in srgb,
        token('neutral-pressed') 32%,
        token('surf-cont-tertiary')
      );
    }
  }
}
</style>
